import { ShieldAlert } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, buttonStyles } from "../../components/ui";

export const SESSION_IDLE_LIMIT_MS = 5 * 60 * 1000;
export const SESSION_IDLE_WARNING_MS = 60 * 1000;

function activityKey(userId: string) {
  return `munipiri:session:last-activity:${userId}`;
}

export function readSessionActivity(userId: string): number | null {
  try {
    const value = Number(window.localStorage.getItem(activityKey(userId)) ?? 0);
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

export function markSessionActivity(userId: string, at = Date.now()) {
  try {
    window.localStorage.setItem(activityKey(userId), String(at));
  } catch {
    // El control permanece activo en memoria aunque el navegador bloquee storage.
  }
  return at;
}

export function clearSessionActivity(userId: string) {
  try {
    window.localStorage.removeItem(activityKey(userId));
  } catch {
    // La sesión igualmente se invalida en Supabase.
  }
}

export function sessionIsExpired(userId: string, now = Date.now()) {
  const lastActivity = readSessionActivity(userId);
  return lastActivity !== null && now - lastActivity >= SESSION_IDLE_LIMIT_MS;
}

export function SessionIdleGuard({
  userId,
  onExpire,
}: {
  userId: string;
  onExpire: () => void | Promise<void>;
}) {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const lastActivityRef = useRef(0);
  const lastWriteRef = useRef(0);
  const warningVisibleRef = useRef(false);
  const expiringRef = useRef(false);
  const continueRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    warningVisibleRef.current = secondsLeft !== null;
  }, [secondsLeft]);

  const expire = useCallback(() => {
    if (expiringRef.current) return;
    expiringRef.current = true;
    setSecondsLeft(null);
    void onExpire();
  }, [onExpire]);

  const keepAlive = useCallback(() => {
    const now = markSessionActivity(userId);
    lastActivityRef.current = now;
    lastWriteRef.current = now;
    warningVisibleRef.current = false;
    setSecondsLeft(null);
  }, [userId]);

  useEffect(() => {
    const storedActivity = readSessionActivity(userId);
    const initialActivity = storedActivity ?? markSessionActivity(userId);
    lastActivityRef.current = initialActivity;
    lastWriteRef.current = initialActivity;
    expiringRef.current = false;

    const evaluate = () => {
      const persisted = readSessionActivity(userId);
      if (persisted !== null) lastActivityRef.current = persisted;
      const remaining =
        SESSION_IDLE_LIMIT_MS - (Date.now() - lastActivityRef.current);

      if (remaining <= 0) {
        expire();
        return;
      }

      if (remaining <= SESSION_IDLE_WARNING_MS) {
        setSecondsLeft(Math.max(1, Math.ceil(remaining / 1000)));
      } else {
        setSecondsLeft(null);
      }
    };

    const recordActivity = () => {
      if (warningVisibleRef.current || expiringRef.current) return;
      const now = Date.now();
      if (now - lastWriteRef.current < 10_000) return;
      lastActivityRef.current = markSessionActivity(userId, now);
      lastWriteRef.current = now;
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== activityKey(userId) || !event.newValue) return;
      const next = Number(event.newValue);
      if (!Number.isFinite(next)) return;
      lastActivityRef.current = next;
      warningVisibleRef.current = false;
      setSecondsLeft(null);
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") evaluate();
    };

    window.addEventListener("pointerdown", recordActivity, { passive: true });
    window.addEventListener("keydown", recordActivity);
    window.addEventListener("touchstart", recordActivity, { passive: true });
    window.addEventListener("storage", handleStorage);
    document.addEventListener("visibilitychange", handleVisibility);
    const timer = window.setInterval(evaluate, 1000);
    evaluate();

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("pointerdown", recordActivity);
      window.removeEventListener("keydown", recordActivity);
      window.removeEventListener("touchstart", recordActivity);
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [expire, userId]);

  useEffect(() => {
    if (secondsLeft === null) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    continueRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [secondsLeft]);

  if (secondsLeft === null) return null;

  return (
    <div className="confirmation-backdrop session-idle-backdrop" role="presentation">
      <section
        className="confirmation-dialog session-idle-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-idle-title"
        aria-describedby="session-idle-description"
      >
        <span className="confirmation-icon session-idle-icon">
          <ShieldAlert aria-hidden="true" />
        </span>
        <div>
          <p className="eyebrow">Sesión protegida</p>
          <h2 id="session-idle-title">¿Seguís en línea?</h2>
          <p id="session-idle-description" aria-live="polite">
            Por seguridad, tu sesión se cerrará en <strong>{secondsLeft}</strong> segundos
            por inactividad.
          </p>
        </div>
        <div className="confirmation-actions">
          <Button type="button" variant="secondary" onClick={expire}>
            Cerrar sesión ahora
          </Button>
          <button
            ref={continueRef}
            className={buttonStyles.primary}
            type="button"
            onClick={keepAlive}
          >
            Sí, sigo en línea
          </button>
        </div>
      </section>
    </div>
  );
}
